import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  fetchDebugLogs,
  selectDebugLogs,
  selectDebugLogsStatus,
} from 'modules/aktien/features/debugLogsSlice';
import { formatDatum, formatProz } from 'core/utils/format';

/** Formatiert einen ISO-Zeitstempel als deutsches Datum + Uhrzeit. */
function formatZeit(value) {
  if (!value) return '–';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleString('de-DE');
}

/** Farbiges Etikett je Log-Typ. */
function TypBadge({ typ }) {
  const istSprung = typ === 'KURSSPRUNG';
  const farbe = istSprung
    ? 'bg-loss/10 text-loss ring-loss/25'
    : 'bg-brand-500/10 text-brand-700 ring-brand-500/25';
  const text = istSprung ? 'Kurssprung' : 'History-Diff';
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ring-1 ${farbe}`}>
      {text}
    </span>
  );
}

/**
 * Ausklappbarer Debug-Bereich der Uebersichtsseite: zeigt die Daten-Check-Logs
 * (History-Differenzen und Kursspruenge), die die Abruf-Scheduler schreiben.
 * Wird erst beim Aufklappen geladen und laesst sich manuell aktualisieren.
 */
export default function DebugLogPanel() {
  const dispatch = useDispatch();
  const logs = useSelector(selectDebugLogs);
  const status = useSelector(selectDebugLogsStatus);
  const [offen, setOffen] = useState(false);

  useEffect(() => {
    if (offen && status === 'idle') {
      dispatch(fetchDebugLogs());
    }
  }, [offen, status, dispatch]);

  return (
    <section className="mx-auto mt-16 max-w-4xl">
      <div className="flex items-center justify-between border-t border-line pt-6">
        <button
          type="button"
          onClick={() => setOffen((o) => !o)}
          className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-ink-muted hover:text-ink"
        >
          <span>{offen ? '▾' : '▸'}</span> Debug – Daten-Check-Logs
        </button>
        {offen && (
          <button
            type="button"
            onClick={() => dispatch(fetchDebugLogs())}
            className="rounded-lg border border-line px-3 py-1 text-xs text-ink-soft hover:bg-ink/[0.03]"
          >
            Aktualisieren
          </button>
        )}
      </div>

      {offen && (
        <div className="mt-4">
          {status === 'loading' && <p className="text-sm text-ink-muted">Lade Logs…</p>}
          {status === 'failed' && (
            <p className="rounded-xl border border-dashed border-loss/30 bg-loss/5 p-6 text-center text-sm text-loss">
              Die Logs konnten nicht geladen werden.
            </p>
          )}
          {status === 'succeeded' && logs.length === 0 && (
            <p className="rounded-xl border border-dashed border-line bg-paper-soft p-6 text-center text-sm text-ink-muted">
              Keine Daten-Check-Einträge vorhanden.
            </p>
          )}
          {status === 'succeeded' && logs.length > 0 && (
            <div className="overflow-x-auto rounded-xl border border-line bg-paper-soft">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="border-b border-line bg-paper-sunken text-left font-mono text-xs uppercase tracking-wider text-ink-muted">
                    <th className="px-4 py-2.5">Zeitpunkt</th>
                    <th className="px-4 py-2.5">Typ</th>
                    <th className="px-4 py-2.5">Aktie</th>
                    <th className="px-4 py-2.5">Tag</th>
                    <th className="px-4 py-2.5 text-right">Alt → Neu</th>
                    <th className="px-4 py-2.5 text-right">Abw.</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((l) => (
                    <tr key={l.id} className="border-t border-line">
                      <td className="whitespace-nowrap px-4 py-3 font-mono text-xs text-ink-muted">
                        {formatZeit(l.erstelltAm)}
                      </td>
                      <td className="px-4 py-3">
                        <TypBadge typ={l.typ} />
                      </td>
                      <td className="px-4 py-3">
                        <div className="truncate font-medium text-ink">{l.aktienName || l.isin}</div>
                        <div className="font-mono text-xs text-ink-muted">{l.isin}</div>
                      </td>
                      <td className="px-4 py-3 text-ink-soft">{formatDatum(l.datum)}</td>
                      <td className="px-4 py-3 text-right font-mono tabular-nums text-ink-soft">
                        {l.altWert != null ? l.altWert : '–'} → {l.neuWert != null ? l.neuWert : '–'}
                      </td>
                      <td className="px-4 py-3 text-right font-mono tabular-nums text-ink">
                        {formatProz(l.abweichungProz)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
